import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { GraduationCap, Search, Star, ShieldCheck, Globe2, Languages, Wand2, CalendarClock, X, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export const BADGE_META = {
  verified_identity: {
    dot: "🟢", label: "Verified Identity",
    info: "Government ID checked and confirmed by our review team.",
  },
  verified_qualifications: {
    dot: "🔵", label: "Verified Qualifications",
    info: "Teaching certificates and diplomas reviewed and authenticated.",
  },
  verified_ijazah: {
    dot: "🟣", label: "Verified Ijazah",
    info: "Holds a certified chain of transmission (ijazah) for Qur'an recitation.",
  },
  institution_verified: {
    dot: "🟡", label: "Institution Verified",
    info: "Employment at the listed institution has been confirmed.",
  },
  master_teacher: {
    dot: "⭐", label: "Master Teacher",
    info: "Consistently high student ratings over an extended teaching record.",
  },
};

/* Small badge pill with a hover/focus tooltip explaining what it means (N8).
   Uses a CSS-only group-hover popover rather than a JS tooltip library so it
   works inside the teacher card's <Link> without hijacking the click, and
   `title` carries the same text for keyboard/screen-reader users. */
export const TeacherBadge = ({ badge }) => {
  const { tx } = useLanguage();
  const meta = BADGE_META[badge];
  if (!meta) return null;
  return (
    <span className="relative group/badge inline-flex" data-testid={`teacher-badge-${badge}`}>
      <span
        className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 inline-flex items-center gap-0.5 cursor-help"
        tabIndex={0}
        title={meta.info}
      >
        {meta.dot} {tx(meta.label)}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-52 rounded-lg bg-forest-700 text-sand-100 text-[10px] leading-snug px-2.5 py-2 shadow-lg opacity-0 group-hover/badge:opacity-100 group-focus-within/badge:opacity-100 transition-opacity z-30"
        data-testid={`teacher-badge-tip-${badge}`}
      >
        {meta.info}
      </span>
    </span>
  );
};

export const Stars = ({ value, count }) => {
  const { t: tr } = useLanguage();
  return (
  <span className="inline-flex items-center gap-1 text-xs">
    <Star className="h-3.5 w-3.5 fill-gold-500 text-gold-500" />
    {value != null ? <><strong>{value}</strong>{count != null && <span className="text-muted-foreground">({count})</span>}</> : <span className="text-muted-foreground">{tr("teachers.newRating")}</span>}
  </span>
  );
};

const selCls = "rounded-xl border border-sand-300 bg-white px-2.5 py-1.5 text-xs";

const QUICK_CHIPS = [
  { label: "👩‍🏫 Women's Instructors", filters: { category: "Women's Instructor" } },
  { label: "🧒 Kids Specialists", filters: { category: "Kids Specialist" } },
  { label: "🟣 Ijazah Holders", filters: { badge: "verified_ijazah" } },
];

const MATCH_GOALS = ["Hifz Teacher", "Quran Fluency Teacher", "Tajweed Teacher", "Arabic Teacher", "Tafsir Teacher", "Kids Specialist", "Family Quran Coach"];

function MatchFinder({ onClose }) {
  const { t: tr, tx } = useLanguage();
  const [answers, setAnswers] = useState({ age_group: "Adults", language: "", goal_category: "Hifz Teacher", teacher_gender: "", country: "" });
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/teachers/match", answers);
      setMatches(r.data.matches);
    } catch { toast.error(tr("teachers.matchingFailedTryAgain")); }
    finally { setBusy(false); }
  };
  return (
    <div className="hifz-card border-gold-500/40 space-y-4" data-testid="match-finder">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg text-forest-700 inline-flex items-center gap-2"><Wand2 className="h-4 w-4 text-gold-600" /> {tr("teachers.findMyPerfectMatch")}</h2>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="match-close"><X className="h-4 w-4" /></button>
      </div>
      {!matches ? (
        <>
          <div className="grid gap-3">
            <label className="text-xs font-semibold text-forest-700">{tr("teachers.whoIsLearning")}
              <select className={`${selCls} w-full mt-1`} value={answers.age_group} onChange={(e) => set("age_group", e.target.value)} data-testid="match-age">
                <option value="Children">{tr("teachers.children")}</option><option value="Teenagers">{tr("teachers.teenagers")}</option><option value="Adults">{tr("teachers.adults")}</option><option value="Families">{tr("teachers.families")}</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-forest-700">{tr("teachers.learningGoal")}
              <select className={`${selCls} w-full mt-1`} value={answers.goal_category} onChange={(e) => set("goal_category", e.target.value)} data-testid="match-goal">
                {MATCH_GOALS.map((g) => <option key={g} value={g}>{tx(g)}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-forest-700">{tr("teachers.preferredLanguage")}
              <input className={`${selCls} w-full mt-1`} placeholder={tr("teachers.eGEnglish")} value={answers.language} onChange={(e) => set("language", e.target.value)} data-testid="match-language" />
            </label>
            <label className="text-xs font-semibold text-forest-700">{tr("teachers.preferredTeacherGender")}
              <select className={`${selCls} w-full mt-1`} value={answers.teacher_gender} onChange={(e) => set("teacher_gender", e.target.value)} data-testid="match-gender">
                <option value="">{tr("teachers.noPreference")}</option><option value="Male">{tr("teachers.male")}</option><option value="Female">{tr("teachers.female")}</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-forest-700">{tr("teachers.yourCountryOptional")}
              <input className={`${selCls} w-full mt-1`} value={answers.country} onChange={(e) => set("country", e.target.value)} data-testid="match-country" />
            </label>
          </div>
          <button onClick={run} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="match-run">
            <Wand2 className="h-3.5 w-3.5" /> {busy ? tr("teachers.matching") : tr("teachers.showMyTop5Matches")}
          </button>
        </>
      ) : matches.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="match-empty">{tr("teachers.noPublicTeachersToMatch")}</p>
      ) : (
        <div className="space-y-2" data-testid="match-results">
          {matches.map((m) => (
            <Link key={m.user_id} to={`/app/teachers/${m.user_id}`} className="flex items-center gap-3 rounded-xl bg-sand-200/50 p-3 hover:bg-sand-200 transition" data-testid={`match-${m.user_id}`}>
              <div className="h-10 w-10 shrink-0 rounded-xl bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold">{(m.name || "T")[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-forest-700 truncate">{m.name} <span className="text-xs text-muted-foreground">· {m.country}</span></div>
                <div className="text-[11px] text-muted-foreground truncate">{(m.match_reasons || []).join(" · ")}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-semibold text-gold-600">{m.compatibility}%</div>
                <div className="text-[9px] uppercase text-muted-foreground">{tr("teachers.match")}</div>
              </div>
            </Link>
          ))}
          <button onClick={() => setMatches(null)} className="btn-ghost text-xs" data-testid="match-again">{tr("teachers.adjustAnswers")}</button>
        </div>
      )}
    </div>
  );
}

export default function Teachers() {
  const { tx, t: tr } = useLanguage();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ country: "", city: "", language: "", gender: "", category: "", badge: "", mode: "" });
  const [showMatch, setShowMatch] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    api.get(`/teachers?${params}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="teachers-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{tr("teachers.globalQuranTeachersNetwork")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{tr("teachers.findYourMentor")}</h1>
        <p className="mt-2 text-foreground/70">{tr("teachers.verifiedQualifiedEducatorsChosenFor")}</p>
        <div className="mt-3 flex flex-nowrap items-center gap-1.5 overflow-x-auto no-scrollbar">
          <button onClick={() => setShowMatch((s) => !s)} className="btn-accent text-xs px-3 py-1.5 gap-1 shrink-0" data-testid="open-match-finder">
            <Wand2 className="h-3.5 w-3.5" /> {tr("teachers.findMyMatch")}
          </button>
          <Link to="/app/bookings" className="btn-ghost text-xs px-3 py-1.5 gap-1 shrink-0" data-testid="my-bookings-link">
            <CalendarClock className="h-3.5 w-3.5" /> {tr("teachers.myBookings")}
          </Link>
          {/* C8 — Voice Notes no longer has its own sidebar entry; this is
              its one entry point now. */}
          <Link to="/app/voice-notes" className="btn-ghost text-xs px-3 py-1.5 gap-1 shrink-0" data-testid="voice-notes-link">
            <MessageCircle className="h-3.5 w-3.5" /> {tr("teachers.voiceNotes")}
          </Link>
        </div>
      </header>

      <div className="hifz-card flex items-center justify-between gap-3 bg-forest-700 text-sand-100 p-4" data-testid="become-teacher-cta">
        <div className="font-display font-semibold text-sm truncate">{tr("teachers.quranTeacher")}</div>
        <Link to="/app/teacher-apply" className="btn-accent text-xs shrink-0" data-testid="cta-apply">
          <ShieldCheck className="h-3.5 w-3.5" /> {tr("teachers.getVerified")}
        </Link>
      </div>

      {showMatch && <MatchFinder onClose={() => setShowMatch(false)} />}

      {/* Quick filters */}
      <div className="flex flex-nowrap gap-1.5" data-testid="quick-chips">
        {QUICK_CHIPS.map((c) => {
          const active = Object.entries(c.filters).every(([k, v]) => filters[k] === v);
          return (
            <button key={c.label}
              onClick={() => setFilters((f) => {
                const cleared = { country: f.country, city: f.city, language: f.language, gender: f.gender, category: "", badge: "", mode: f.mode };
                return active ? cleared : { ...cleared, ...c.filters };
              })}
              className={`flex-1 min-w-0 truncate px-1.5 py-1.5 rounded-full text-[11px] font-medium border transition ${
                active ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300 bg-white hover:bg-sand-200"}`}
              data-testid={`quick-chip-${c.filters.category || c.filters.badge}`}>
              {tx(c.label)}
            </button>
          );
        })}
      </div>

      <div className="hifz-card space-y-3" data-testid="teachers-filters">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("teachers.searchByNameCountryInstitution")}
            className="w-full rounded-xl border border-sand-300 bg-white pl-9 pr-3 py-2 text-sm" data-testid="teachers-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <select className={selCls} value={filters.country} onChange={(e) => setF("country", e.target.value)} data-testid="filter-country">
            <option value="" hidden>{tr("profile.country")}</option>
            {[...new Set(data?.facets?.countries || [])].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className={selCls} value={filters.city} onChange={(e) => setF("city", e.target.value)} data-testid="filter-city">
            <option value="" hidden>{tr("profile.city")}</option>
            {[...new Set(data?.facets?.cities || [])].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className={selCls} value={filters.language} onChange={(e) => setF("language", e.target.value)} data-testid="filter-language">
            <option value="" hidden>{tr("teachers.language")}</option>
            {[...new Set(data?.facets?.languages || [])].map((l) => <option key={l}>{l}</option>)}
          </select>
          <select className={selCls} value={filters.gender} onChange={(e) => setF("gender", e.target.value)} data-testid="filter-gender">
            <option value="" hidden>{tr("profile.gender")}</option>
            <option value="Male">{tr("teachers.male")}</option><option value="Female">{tr("teachers.female")}</option>
          </select>
          <select className={selCls} value={filters.category} onChange={(e) => setF("category", e.target.value)} data-testid="filter-category">
            <option value="" hidden>{tr("teachers.category")}</option>
            {[...new Set(data?.facets?.categories || [])].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className={selCls} value={filters.badge} onChange={(e) => setF("badge", e.target.value)} data-testid="filter-badge">
            <option value="" hidden>{tr("teachers.verification")}</option>
            {[...new Set(data?.facets?.badges || [])].map((b) => <option key={b} value={b}>{BADGE_META[b]?.label || b}</option>)}
          </select>
          <select className={selCls} value={filters.mode} onChange={(e) => setF("mode", e.target.value)} data-testid="filter-mode">
            <option value="" hidden>{tr("teachers.mode")}</option>
            <option value="Online">{tr("teachers.online")}</option><option value="Offline">{tr("teachers.offline")}</option><option value="Hybrid">{tr("teachers.hybrid")}</option>
          </select>
          {Object.values(filters).some(Boolean) && (
            <button
              type="button"
              onClick={() => setFilters({ country: "", city: "", language: "", gender: "", category: "", badge: "", mode: "" })}
              className="text-xs text-forest-700 hover:underline px-1"
              data-testid="filter-clear-all"
            >
              {tr("teachers.clearFilters")}
            </button>
          )}
        </div>
      </div>

      {!data ? (
        <div className="hifz-card h-48 animate-pulse-soft bg-sand-200/50" />
      ) : data.teachers.length === 0 ? (
        <div className="hifz-card border-dashed text-center p-10" data-testid="teachers-empty">
          <GraduationCap className="h-8 w-8 mx-auto text-gold-600" />
          <p className="mt-2 text-sm text-foreground/70">{tr("teachers.noVerifiedTeachersMatchThese")}</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="teachers-grid">
          {data.teachers.map((t) => (
            <Link key={t.user_id} to={`/app/teachers/${t.user_id}`}
              className="hifz-card hifz-card-hover block" data-testid={`teacher-card-${t.user_id}`}>
              <div className="flex items-start gap-3">
                <div className="h-14 w-14 shrink-0 rounded-2xl bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold text-xl">
                  {(t.name || "T")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display font-semibold text-forest-700 truncate">{t.name}</div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {t.status === "featured" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-700 font-semibold uppercase">{tr("teachers.featured")}</span>}
                      {t.hourly_rate != null && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold-500/15 text-gold-600 font-semibold" data-testid={`teacher-rate-${t.user_id}`}>
                          ${t.hourly_rate}/hr
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Globe2 className="h-3 w-3" />{t.country || "—"}</span>
                    <span className="inline-flex items-center gap-1"><Languages className="h-3 w-3" />{t.languages.slice(0, 2).join(", ")}</span>
                    {t.years_teaching && <span>{tr("teachers.yExp", { years_teaching: t.years_teaching })}</span>}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <Stars value={t.rating} count={t.reviews_count} />
                    {t.recitation_overall != null && <span className="text-xs text-muted-foreground">{tr("teachers.ai", { recitation_overall: t.recitation_overall })}</span>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {t.categories.slice(0, 3).map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-sand-200">{c}</span>)}
                {t.badges.map((b) => <TeacherBadge key={b} badge={b} />)}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
